import { useNavigation } from "@react-navigation/native";
import { useMutation } from "@tanstack/react-query";
import { Divider, Text } from "@ui-kitten/components";
import classNames from "classnames";
import Button from "components/Button";
import Container from "components/Container";
import { isDev } from "config/env";
import { BillingInterval, SignupChargeDetails } from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import { getSanityImage } from "lib/sanity";
import { CmsPlan, ShopPage } from "lib/sanity.types";
import { fetchSignupChargeRequest } from "queries/account.queries";
import { trackConversion } from "queries/conversion.queries";
import { useFetchPlanPackagesQuery } from "queries/planPackage.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import React, { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import { formatCurrency } from "utils/appUtils";
import { toDashboard } from "utils/navigationUtils";
import PortableText from "./components/PortableText";
import SectionTitle from "./components/SectionTitle";
import Tabs, { TabList, TabPanel, TabPanels } from "./components/Tabs";

interface Prices {
  [key: number]: {
    MONTHLY?: string;
    FULL_SEASON?: string;
  };
}

export interface PlanDetailProps {
  plans: ShopPage["plans"];
  primaryColor: string;
  secondaryColor: string;
  recommendedText: string;
  signupCharges: Prices;
  onPurchasePress: (plan: CmsPlan) => void;
  onGiftPress: (plan: CmsPlan) => void;
}

const PurchaseButton = ({
  backgroundColor,
  buttonText,
  onPress,
}: {
  backgroundColor: string;
  onPress: () => void;
  buttonText: string;
}) => (
  <Button
    onPress={onPress}
    style={{
      backgroundColor,
      borderWidth: 0,
    }}
  >
    {buttonText}
  </Button>
);

function PlanDetails({
  plans: shopPlans,
  primaryColor,
  secondaryColor,
  recommendedText,
  signupCharges,
  onPurchasePress,
  onGiftPress,
}: PlanDetailProps) {
  const { data: planPackageDetails } = useFetchPlanPackagesQuery();
  const idPropertyName = isDev ? "demoId" : "prodId";
  const findPlanById = (id: number) => {
    return planPackageDetails?.find((planPackage) => planPackage.id === id);
  };

  const getPriceByPlan = (id: any, billingInterval: BillingInterval) => {
    if (!signupCharges) return 0;
    const price = signupCharges[id]?.[billingInterval];
    if (!price) return 0;
    return price;
  };

  const shouldShowMonthlyPrice = (plan: CmsPlan) => {
    // Do not show monthly price if the plan is gift only
    if (!plan.purchaseEnabled && plan.giftingEnabled) return false;
    return true;
  };

  return (
    <View
      className={classNames("gap-4", {
        "gap-4 grid place-items-center md:grid-cols-3": shopPlans.length >= 3,
        "flex md:flex-row justify-center items-stretch": shopPlans.length < 3,
      })}
    >
      {shopPlans.map((plan) => {
        const planPackageDetail = findPlanById(Number(plan[idPropertyName]));

        return (
          <View
            key={plan._key}
            className="flex-col p-10 bg-white rounded-lg relative overflow-hidden max-w-2xl"
          >
            {plan.recommended && (
              <View
                className="absolute -right-4 top-6 rotate-45 w-24"
                style={{
                  backgroundColor: plan.planColor?.hex || secondaryColor,
                }}
              >
                <Text
                  style={{ fontSize: 10, textAlign: "center" }}
                  status="control"
                >
                  {recommendedText}
                </Text>
              </View>
            )}

            <Text category="h6">{plan.name}</Text>

            {planPackageDetail && (
              <View className="space-y-4 py-5">
                {shouldShowMonthlyPrice(plan) && (
                  <>
                    {planPackageDetail.billingIntervals.includes("MONTHLY") && (
                      <div>
                        <span className="font-bold text-3xl">
                          {formatCurrency(
                            getPriceByPlan(plan[idPropertyName], "MONTHLY")
                          )}
                        </span>{" "}
                        <span className="text-xs uppercase">
                          {plan.monthlyPriceSuffix}
                        </span>
                      </div>
                    )}
                    {planPackageDetail.billingIntervals.length > 1 && (
                      <div>OR</div>
                    )}
                  </>
                )}
                {planPackageDetail.billingIntervals.includes("FULL_SEASON") && (
                  <div>
                    <span className="font-bold text-3xl">
                      {formatCurrency(
                        getPriceByPlan(plan[idPropertyName], "FULL_SEASON")
                      )}{" "}
                    </span>
                    <span className="text-xs uppercase">
                      {plan.payInFullPriceSuffix}
                    </span>
                  </div>
                )}
              </View>
            )}
            <Divider style={{ marginVertical: 10 }} />

            <View></View>

            <View className="gap-4 flex-wrap">
              <PortableText value={plan.description}></PortableText>
            </View>

            <View className="mt-auto">
              {plan.purchaseEnabled && (
                <View className="mt-4">
                  <PurchaseButton
                    backgroundColor={plan.planColor?.hex || primaryColor}
                    onPress={() => onPurchasePress(plan)}
                    buttonText={plan.purchaseButtonText || "Subscribe"}
                  />
                </View>
              )}

              {plan.giftingEnabled && (
                <View className="mt-4">
                  <PurchaseButton
                    backgroundColor={plan.planColor?.hex || primaryColor}
                    onPress={() => onGiftPress(plan)}
                    buttonText={plan.giftButtonText || "Purchase as a Gift"}
                  />
                </View>
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
}

const Plans = ({ shopPage }: { shopPage: ShopPage }) => {
  const storedUserId = useAppSelector((state) => state.auth.id);
  const navigation = useNavigation();

  const { data: branding } = useFetchTenantBrandingQuery();
  const [selectedGroup, setSelectedGroup] = useState<string>();

  /**
    {
      1:{
        MONTHLY:35,
        FULL_SEASON:700
      },
      2:{
        MONTHLY:40,
        FULL_SEASON:800
      },
      ...etc
    }
  
  */
  const [signupCharges, setSignupCharges] = useState<Prices>([]);

  const { data: planPackageDetails } = useFetchPlanPackagesQuery();
  const { mutateAsync: fetchSignupCharge } = useMutation(
    fetchSignupChargeRequest
  );
  const { mutateAsync: track } = useMutation(trackConversion);

  const availablePlanGroups = Array.from(
    new Set(shopPage.plans.map((plan) => plan.group))
  );

  const getPlansByGroupName = (groupName: string) => {
    return shopPage.plans.filter((plan) => plan.group === groupName);
  };

  const findPlanColorByGroupName = () => {
    if (!selectedGroup) return undefined;
    const plans = getPlansByGroupName(selectedGroup);
    return plans[0].planColor?.hex || branding?.secondaryColor;
  };

  const seatMapUrl = useMemo(() => {
    const seatMap = shopPage.seatMaps.find(
      (seatMap) => seatMap.group === selectedGroup
    );
    if (!seatMap) return;
    return getSanityImage(seatMap.seatMapImage.asset._ref);
  }, [selectedGroup]);

  const idPropertyName = isDev ? "demoId" : "prodId";
  const findPlanById = (id: number) => {
    return planPackageDetails?.find((planPackage) => planPackage.id === id);
  };

  const onPurhcaseBtnPress = async (plan: CmsPlan) => {
    if (storedUserId) {
      toDashboard();
      return;
    }
    await track({ step: "PURCHASE_CLICKED", flow: "SHOP_PHASE_ONE" });
    navigation.navigate("LoginFlow", {
      screen: "Register",
      params: { preSelectedPlanPackageId: String(plan[idPropertyName]) },
    });
  };

  const onGiftingBtnPress = async (plan: CmsPlan) => {
    await track({ step: "PURCHASE_GIFT_CLICKED", flow: "SHOP_PHASE_ONE" });
    navigation.navigate("BuyGift", {
      planPackageId: plan[idPropertyName],
    });
  };

  const signupFees = (signupCharge: SignupChargeDetails): number => {
    return signupCharge.signupFees?.reduce((acc, fee) => acc + Number(fee.feeAmount), 0) || 0;
  };

  const recurringFees = (signupCharge: SignupChargeDetails): number => {
    return signupCharge.recurringFees?.reduce((acc, fee) => acc + Number(fee.feeAmount), 0) || 0;
  };

  useEffect(() => {
    setSelectedGroup(availablePlanGroups[0]);
  }, []);

  useEffect(() => {
    if (!planPackageDetails) return;
    const fetchSignupCharges = async () => {
      // fetch the monthly price and full price if applicacble
      for (const plan of shopPage.plans) {
        const planPackage = findPlanById(Number(plan[idPropertyName]));
        if (!planPackage) return;
        for (const billingInterval of planPackage.billingIntervals) {
          const signupCharge = await fetchSignupCharge({
            billingInterval,
            planPackageIds: [planPackage.id],
          });
          setSignupCharges((signupCharges) => {
            const planSignupCharge = signupCharges?.[planPackage.id];
            const newSignupCharges = {
              ...signupCharges,
              [planPackage.id]: {
                ...planSignupCharge,
                [billingInterval]:
                  billingInterval === "FULL_SEASON"
                    ? Number(signupCharge.signupCharge) + signupFees(signupCharge)
                    : Number(signupCharge.recurringCharge) + recurringFees(signupCharge) + signupFees(signupCharge),
              },
            };
            return newSignupCharges;
          });
        }
      }
    };

    fetchSignupCharges();
  }, [planPackageDetails]);

  return (
    <View className="px-4 md:px-16 py-4 bg-slate-100" nativeID="plans">
      <Container className="relative">
        <SectionTitle title="Plans" color={branding!.primaryColor} />
        <View
          className={`grid grid-cols-1 gap-4 place-items-center ${
            shopPage.membershipDescription ? "md:grid-cols-2" : ""
          }`}
        >
          {shopPage.membershipDescription !== undefined && (
            <View>
              <PortableText
                value={shopPage.membershipDescription}
              ></PortableText>
            </View>
          )}
          <img src={seatMapUrl} />
        </View>

        <Tabs className="mt-6">
          {availablePlanGroups.length > 1 && (
            <TabList
              tabs={availablePlanGroups}
              className="sticky md:static top-0 left-0 right-0 z-10"
              onTabPress={(index: number) =>
                setSelectedGroup(availablePlanGroups[index])
              }
              activeTabStyle={{
                backgroundColor: findPlanColorByGroupName(),
              }}
            ></TabList>
          )}

          <TabPanels>
            {availablePlanGroups.map((group, index) => {
              const plans = getPlansByGroupName(group);
              return (
                <TabPanel key={group + index}>
                  <PlanDetails
                    plans={plans}
                    primaryColor={branding!.primaryColor}
                    secondaryColor={branding!.secondaryColor}
                    recommendedText={shopPage.recommendedText}
                    signupCharges={signupCharges}
                    onPurchasePress={onPurhcaseBtnPress}
                    onGiftPress={onGiftingBtnPress}
                  />
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      </Container>
    </View>
  );
};

export default Plans;
